<template>
  <div class="pair-game">
      <div class="game-header">
        <div class="game-header-content">
          <div class="game-title">{{ $t('matchingGamePage.title') }}</div>
          <p class="game-instructions">{{ $t('matchingGamePage.instructions') }}</p>
        </div>
      </div>

      <div
        ref="board"
        class="board"
        @pointermove="moveDrag"
        @pointerup="endDrag"
        @pointercancel="cancelDrag"
      >
        <svg
          class="connections"
          :viewBox="`0 0 ${boardSize.width} ${boardSize.height}`"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <line
            v-for="line in completedLines"
            :key="line.id"
            :x1="line.x1"
            :y1="line.y1"
            :x2="line.x2"
            :y2="line.y2"
            class="completed-line"
          />
          <line
            v-if="activeLine"
            :x1="activeLine.x1"
            :y1="activeLine.y1"
            :x2="activeLine.x2"
            :y2="activeLine.y2"
            class="active-line"
          />
        </svg>

        <div class="column">
          <button
            v-for="item in leftItems"
            :key="item.id"
            :ref="setLeftRef(item.id)"
            type="button"
            class="object-button left-item"
            :class="{
              selected: selectedItemId === item.id,
              dragging: draggingId === item.id,
              matched: isLeftMatched(item.id)
            }"
            :disabled="isLeftMatched(item.id) || isSubmitting"
            @click="selectItem(item.id)"
            @pointerdown.prevent="startDrag($event, item.id)"
          >
            <img class="object-icon" :src="item.image" :alt="item.label">
            <strong>{{ item.label }}</strong>
          </button>
        </div>
        <div class="column">
          <button
            v-for="item in rightItems"
            :key="item.id"
            :ref="setRightRef(item.id)"
            type="button"
            class="object-button right-item"
            :class="{ matched: isRightMatched(item.id) }"
            :data-target-id="item.id"
            :disabled="isRightMatched(item.id) || isSubmitting"
            @click="selectTarget(item.id)"
          >
            <img class="object-icon" :src="item.image" :alt="item.label">
            <strong>
              <span v-for="(line, index) in item.labelLines" :key="`${item.id}-line-${index}`">
                {{ line }}
              </span>
            </strong>
          </button>
        </div>
      </div>
      
      <button type="button" class="small-button" @click="resetGame">
        <img class="play-again-icon" src="@/assets/playAgain.svg" alt="" aria-hidden="true">
        <span>{{ $t('matchingGamePage.restartButton') }}</span>
      </button>
      <button type="button" class="submit-button" :disabled="!isComplete || isSubmitting" @click="submitMatches">
        {{ $t('matchingGamePage.submitButton') }}
      </button>
  </div>
</template>

<script>
import ethiopiaImage from '@/assets/matching/ethiopia.png';
import peachImage from '@/assets/matching/peach-e.svg';
import colombiaImage from '@/assets/matching/colombia.png';
import caramelImage from '@/assets/matching/caramel-c.png';
import indonesiaImage from '@/assets/matching/indonesia.png';
import wineyImage from '@/assets/matching/winey-i.png';

export default {
  name: 'ObjectPairMatchingGame',
  props: {
    userInfo: Object
  },
  emits: ['matches-submitted'],
  data() {
    return {
      pairs: [
        {id: 'ehiopia', left: {image: ethiopiaImage, labelKey: 'ethiopia'}, right: {id: 'peach', image: peachImage, labelKey: 'peach'}},
        {id: 'colombia', left: {image: colombiaImage, labelKey: 'colombia'}, right: {id: 'caramel', image: caramelImage, labelKey: 'caramel'}},
        {id: 'indonesia', left: {image: indonesiaImage, labelKey: 'indonesia'}, right: {id: 'winey', image: wineyImage, labelKey: 'winey'}}
      ],
      rightOrder: [],
      selectedItemId: null,
      matchedIds: [],
      matchResults: [],
      draggingId: null,
      activePointerId: null,
      pointerPosition: null,
      boardSize: {
        width: 0,
        height: 0
      },
      completedLines: [],
      leftRefs: {},
      rightRefs: {},
      resizeObserver: null,
      isSubmitting: false
    };
  },
  computed: {
    leftItems() {
      return this.pairs.map(({ id, left }) => ({
        id,
        image: left.image,
        label: this.$t(`matchingGamePage.items.${left.labelKey}`)
      }));
    },
    rightItems() {
      return this.rightOrder.map((id) => {
        const pair = this.pairs.find((item) => item.id === id);
        const labelLines = [
          this.$t(`matchingGamePage.items.${pair.right.labelKey}.line1`),
          this.$t(`matchingGamePage.items.${pair.right.labelKey}.line2`)
        ];

        return {
          ...pair.right,
          label: labelLines.join(' '),
          labelLines
        };
      });
    },
    activeLine() {
      if (!this.draggingId || !this.pointerPosition) return null;

      const start = this.getCenter(this.leftRefs[this.draggingId]);
      if (!start) return null;

      return {
        x1: start.x,
        y1: start.y,
        x2: this.pointerPosition.x,
        y2: this.pointerPosition.y
      };
    },
    isComplete() {
      return this.matchedIds.length === this.pairs.length;
    }
  },
  created() {
    this.resetGame();
  },
  mounted() {
    this.$nextTick(() => {
      this.updateBoard();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateBoard);
        this.resizeObserver.observe(this.$refs.board);
      } else {
        window.addEventListener('resize', this.updateBoard);
      }
    });
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    window.removeEventListener('resize', this.updateBoard);
  },
  methods: {
    shuffle(items) {
      return [...items].sort(() => Math.random() - 0.5);
    },
    resetGame() {
      this.rightOrder = this.shuffle(this.pairs.map(({ id }) => id));
      this.selectedItemId = null;
      this.matchedIds = [];
      this.matchResults = [];
      this.draggingId = null;
      this.activePointerId = null;
      this.pointerPosition = null;
      this.completedLines = [];
      this.$nextTick(this.updateBoard);
    },
    setLeftRef(id) {
      return (element) => {
        if (element) this.leftRefs[id] = element;
      };
    },
    setRightRef(id) {
      return (element) => {
        if (element) this.rightRefs[id] = element;
      };
    },
    isLeftMatched(id) {
      return this.matchedIds.includes(id);
    },
    isRightMatched(id) {
      return this.matchResults.some((match) => match.selectedItemId === id);
    },
    selectItem(id) {
      this.selectedItemId = id;
    },
    selectTarget(targetId) {
      if (!this.selectedItemId) {
        return;
      }

      this.connectItems(this.selectedItemId, targetId);
    },
    connectItems(itemId, targetId) {
      if (
        this.isLeftMatched(itemId) ||
        this.isRightMatched(targetId) ||
        this.isSubmitting
      ) {
        return;
      }

      const pair = this.pairs.find((item) => item.id === itemId);
      const result = {
        itemId,
        selectedItemId: targetId,
        correctItemId: pair.right.id,
        isCorrect: pair.right.id === targetId
      };

      this.matchResults.push(result);
      this.matchedIds.push(itemId);
      this.selectedItemId = null;

      this.$nextTick(this.updateCompletedLines);
    },
    startDrag(event, id) {
      if (this.isLeftMatched(id) || this.isSubmitting) return;

      this.selectedItemId = null;
      this.draggingId = id;
      this.activePointerId = event.pointerId;
      this.pointerPosition = this.getPointerPosition(event);
      if (event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    },
    moveDrag(event) {
      if (
        this.activePointerId === null ||
        event.pointerId !== this.activePointerId
      ) {
        return;
      }

      event.preventDefault();
      this.pointerPosition = this.getPointerPosition(event);
    },
    endDrag(event) {
      if (
        this.activePointerId === null ||
        event.pointerId !== this.activePointerId ||
        !this.draggingId
      ) {
        return;
      }

      event.preventDefault();

      const target = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest('[data-target-id]');
      const targetId = target?.dataset.targetId;
      const itemId = this.draggingId;

      this.clearDrag();

      if (targetId && !this.isRightMatched(targetId)) {
        this.connectItems(itemId, targetId);
      }
    },
    cancelDrag(event) {
      if (
        this.activePointerId === null ||
        event.pointerId !== this.activePointerId
      ) {
        return;
      }

      this.clearDrag();
    },
    clearDrag() {
      this.draggingId = null;
      this.activePointerId = null;
      this.pointerPosition = null;
    },
    getPointerPosition(event) {
      const board = this.$refs.board;
      if (!board) return null;

      const rect = board.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    },
    getCenter(element) {
      const board = this.$refs.board;
      if (!element || !board) return null;

      const boardRect = board.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      return {
        x: elementRect.left - boardRect.left + elementRect.width / 2,
        y: elementRect.top - boardRect.top + elementRect.height / 2
      };
    },
    updateBoard() {
      const board = this.$refs.board;
      if (!board) return;

      this.boardSize = {
        width: board.clientWidth,
        height: board.clientHeight
      };
      this.updateCompletedLines();
    },
    updateCompletedLines() {
      this.completedLines = this.matchResults
        .map((match) => {
          const start = this.getCenter(this.leftRefs[match.itemId]);
          const end = this.getCenter(this.rightRefs[match.selectedItemId]);

          if (!start || !end) return null;

          return {
            id: match.itemId,
            x1: start.x,
            y1: start.y,
            x2: end.x,
            y2: end.y
          };
        })
        .filter(Boolean);
    },
    async submitMatches() {
      if (!this.isComplete || this.isSubmitting) return;

      this.isSubmitting = true;
      try {
        const matches = Object.fromEntries(
          this.matchResults.map(({ itemId, selectedItemId }) => [itemId, selectedItemId])
        );

        const response = await fetch('https://kafea-terra-form-api.ocm.workers.dev/', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            email: this.userInfo.email,
            matches
          })
        });
        const contentType = response.headers.get('content-type') || '';
        let apiResponse = null;

        if (contentType.includes('application/json')) {
          apiResponse = await response.json();
        } else {
          const responseText = await response.text();
          try {
            apiResponse = responseText ? JSON.parse(responseText) : null;
          } catch {
            apiResponse = responseText || null;
          }
        }

        const isNoGiftAvailable = apiResponse &&
          typeof apiResponse === 'object' &&
          apiResponse.error === 'GIFTS_SOLD_OUT';

        if (!response.ok && !isNoGiftAvailable) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        this.$emit('matches-submitted', {
          correctCount: this.matchResults.filter((match) => match.isCorrect).length,
          totalCount: this.pairs.length,
          matches: [...this.matchResults],
          apiResponse
        });
      } catch (error) {
        console.error('Error submitting matches:', error);
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.pair-game {
  padding: 0;
  padding-bottom: 5px;
  color: white;
}

.game-header {
  padding-top: min(5.1852vw, 112px);
}

.game-header-content {
  display: flex;
  flex-direction: column;
  gap: min(2.1296vw, 46px);
}

.game-title,
.game-instructions {
  margin: 0;
  letter-spacing: 0;
  color: #fff;
}

.game-title {
  padding-inline: min(5vw, 216px);
  text-align: center;
  font-size: min(6.4815vw, 140px);
  font-weight: 500;
  line-height: min(7.5463vw, 163px);
  text-transform: uppercase;
}

.game-instructions {
  padding-inline: min(3.9259vw, 84.8px);
  text-align: center;
  font-family: 'CorporateFont';
  font-size: min(3.6111vw, 78px);
  font-weight: 300;
  line-height: min(4.3519vw, 94px);
}

.submit-button {
  border: 0;
  padding: 0;
  color: #fff;
  background: #0090D4;
}

.small-button {
  display: flex;
  align-items: center;
  width: fit-content;
  margin-inline: auto;
  gap: min(0.8333vw, 18px);
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  text-align: center;
  font-family: 'DrukFont', sans-serif;
  font-size: min(4.537vw, 98px);
  font-weight: 500;
  line-height: min(6.2963vw, 136px);
  letter-spacing: 0;
  cursor: pointer;
}

.play-again-icon {
  flex: 0 0 auto;
  width: min(4.0832vw, 88.198px);
  height: min(3.6021vw, 77.806px);
}

.board {
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, min(25.9259vw, 560px));
  justify-content: space-between;
  column-gap: 0;
  width: min(80.9722vw, 1749px);
  margin-inline: auto;
  padding: min(6.0185vw, 130px) 0 36px;
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
}

.connections {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}

.completed-line,
.active-line {
  fill: none;
  stroke: #0090D4;
  stroke-width: min(0.7407vw, 16px);
  stroke-dasharray: min(1.2963vw, 28px) min(0.9259vw, 20px);
  stroke-linecap: round;
  filter: drop-shadow(0 3px 2px rgba(0, 0, 0, .28));
}

.column {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: min(3.2407vw, 70px);
}

.object-button {
  display: grid;
  place-items: center;
  gap: min(0.9259vw, 20px);
  width: 100%;
  height: min(26.8519vw, 580px);
  min-height: 0;
  padding: min(0.3704vw, 8px);
  overflow: hidden;
  border: min(0.7407vw, 16px) solid #C7C7C7;
  border-radius: min(3.7037vw, 80px);
  background: #fff;
  color: #101B53;
  touch-action: none;
  transition: transform 160ms ease, border-color 160ms ease;
}

.left-item {
  cursor: grab;
}

.left-item:active {
  cursor: grabbing;
}

.object-button strong {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  font-family: 'DrukFont', sans-serif;
  font-weight: 500;
  letter-spacing: 0;
  color: #101B53;
}

.left-item strong {
  width: min(7.4537vw, 161px);
  height: min(3.4722vw, 75px);
  justify-content: center;
  justify-self: center;
  text-align: center;
  font-size: min(3.0556vw, 66px);
  line-height: min(3.4722vw, 75px);
  white-space: nowrap;
}

.right-item strong {
  width: min(19.0741vw, 412px);
  height: min(6.4352vw, 139px);
  flex-direction: column;
  justify-content: center;
  text-align: center;
  font-size: min(2.963vw, 64px);
  line-height: min(3.0556vw, 66px);
}

.right-item strong span {
  display: block;
  white-space: nowrap;
}

.object-icon {
  object-fit: contain;
}

.left-item .object-icon {
  width: min(18.8889vw, 408px);
  height: min(18.8426vw, 407px);
}

.right-item .object-icon {
  width: min(17.2222vw, 372px);
  height: min(16.2037vw, 350px);
}

.object-button.selected {
  transform: scale(1.04);
}

.object-button.dragging {
  border-color: #0090D4;
  transform: scale(1.04);
}

.object-button.matched {
  border-color: #0090D4;
}

.submit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(80.9722vw, 1749px);
  height: min(8.5648vw, 185px);
  margin: min(3.1944vw, 69px) auto 24px;
  border-radius: min(4.537vw, 98px);
  text-align: center;
  font-family: 'DrukFont', sans-serif;
  font-size: min(4.8148vw, 104px);
  font-weight: 500;
  line-height: min(5.4167vw, 117px);
  letter-spacing: 0;
  text-transform: uppercase;
}

.submit-button:disabled {
  cursor: not-allowed;
  opacity: .45;
}

@media (min-width: 2390px) {
  .game-title {
    padding-inline: 14vw;
  }
}

@media (max-width: 600px) {
  .board {
    padding: min(6.0185vw, 130px) 0 24px;
  }

}
</style>
